import React, {useEffect, useRef, useState} from 'react';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import SendMessageButton from '../../../components/SendMessageButton';

/**
 * ReadbookAuthorTooltip
 * Shows an author avatar + name and opens a Popper above it with full user info when clicked.
 * Props:
 * - user: { userDisplayName, headshot, agency, office, poc, contact, title, email, aor, organization }
 */
const ReadbookAuthorTooltip = ({user = {}}) => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    const handleToggle = () => setOpen((o) => !o);
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event?.target)) return;
        setOpen(false);
    };

    // close on escape for keyboard users
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const {
        userDisplayName,
        headshot,
        agency,
        office,
        poc,
        contact,
        title: userTitle,
        email,
        aor,
        organization
    } = user || {};

    // Generate a reasonable AoR string when not provided. Prefer explicit aor, otherwise derive from agency/office.
    const generateAor = () => {
        if (aor) return aor;
        // Demo placeholder AoR for demo version
        return 'Global Financial Crime Monitoring';
    };

    const displayOrganization = organization || 'FinCEN';

    return (
        <Box sx={{display: 'inline-flex', alignItems: 'center'}}>
            <Box
                ref={anchorRef}
                onClick={handleToggle}
                sx={{display: 'inline-flex', alignItems: 'center', cursor: 'pointer'}}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : 'false'}
            >
                <Avatar src={headshot} sx={{width: 24, height: 24}}/>
                <Typography variant="body2" sx={{
                    ml: 1,
                    textDecoration: 'underline dotted'
                }}>{userDisplayName || 'Unknown author'}</Typography>
            </Box>

            <Popper open={open} anchorEl={anchorRef.current} placement="top" style={{zIndex: 1400}}>
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper sx={{p: 2, maxWidth: 360}} elevation={3}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Avatar src={headshot} sx={{width: 48, height: 48}}/>
                            <Stack sx={{minWidth: 0}}>
                                <Typography variant="subtitle1" sx={{fontWeight: 600}}
                                            noWrap>{userDisplayName || 'Unknown author'}</Typography>
                                <Typography variant="caption"
                                            color="text.secondary">{userTitle || 'Analyst'}</Typography>
                            </Stack>

                        </Stack>
                        <Stack mt={2} spacing={1}>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}>
                                <Box component="img" src={'/assets/jabber.png'} alt="Jabber"
                                     sx={{width: 16, height: 16}}/>
                                {userDisplayName.split(" ").join(".").toLowerCase()}
                            </Typography>

                            {/* Organization (default to FinCEN if not provided) */}
                            {displayOrganization && (
                                <Typography variant="body2" sx={{mt: 1}}>
                                    <strong>Org:</strong> {displayOrganization}
                                </Typography>
                            )}

                            {/* Area of Responsibility (AoR) - generated when missing */}
                            <Typography variant="body2">
                                <strong>AoR:</strong> {generateAor()}
                            </Typography>

                            {agency && <Typography variant="body2" sx={{mt: 1}}>{agency}</Typography>}
                            {office && <Typography variant="body2">{office}</Typography>}
                            {poc && <Typography variant="body2">POC: {poc}</Typography>}
                            {contact &&
                                <Typography variant="body2" sx={{wordBreak: 'break-all'}}>{contact}</Typography>}
                            {email && <Typography variant="body2"
                                                  sx={{wordBreak: 'break-all'}}>Email: {email}</Typography>}
                            {/* Send message button - create/get one-on-one and add to activeChats */}
                            <Box mt={2}>
                                <SendMessageButton user={user} size="small" variant="contained"
                                                   onOpened={() => setOpen(false)}>
                                    Send message
                                </SendMessageButton>
                            </Box>
                        </Stack>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default ReadbookAuthorTooltip;
